import { useState } from 'react';
import { UserPlus, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, Select } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatDate, timeAgo } from '@/lib/format';
import type { UserRow, UserStatus } from '@/lib/types';
import { useRoles, useUsers } from './api';
import { InviteUserDialog } from './invite-user-dialog';
import { UserDetailDialog } from './user-detail-dialog';

const STATUS_VARIANT: Record<UserStatus, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  ACTIVE: 'success',
  INVITED: 'warning',
  SUSPENDED: 'secondary',
  DEACTIVATED: 'destructive',
};

const STATUS_LABEL: Record<UserStatus, string> = {
  ACTIVE: 'Active',
  INVITED: 'Invited',
  SUSPENDED: 'Suspended',
  DEACTIVATED: 'Deactivated',
};

export function UsersPage() {
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [roleId, setRoleId] = useState('');
  const [sort, setSort] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selected, setSelected] = useState<UserRow | null>(null);

  const debouncedSearch = useDebounce(search);
  const { data: roles = [] } = useRoles();

  const { data, isLoading, error, refetch } = useUsers({
    page: pagination.page,
    limit: pagination.limit,
    q: debouncedSearch || undefined,
    status: status || undefined,
    roleId: roleId || undefined,
    sort: sort || undefined,
  });

  const columns: Column<UserRow>[] = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'name',
      cell: (row) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={row.name} size={30} />
          <div className="min-w-0">
            <div className="truncate font-medium">{row.name}</div>
            <div className="truncate text-xs text-muted-foreground">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'designation',
      header: 'Role at work',
      cell: (row) => (
        <div className="text-sm">
          {row.designation ?? <span className="text-muted-foreground">—</span>}
          {row.department && (
            <div className="text-xs text-muted-foreground">{row.department}</div>
          )}
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Access',
      cell: (row) =>
        row.roles.length === 0 ? (
          <span className="text-xs text-muted-foreground">No roles</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((role) => (
              <Badge key={role.id} variant="secondary">
                {role.name}
              </Badge>
            ))}
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      cell: (row) => <Badge variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</Badge>,
    },
    {
      key: 'lastLoginAt',
      header: 'Last seen',
      sortKey: 'last_login_at',
      className: 'text-sm text-muted-foreground',
      cell: (row) => (row.lastLoginAt ? timeAgo(row.lastLoginAt) : 'Never'),
    },
    {
      key: 'joiningDate',
      header: 'Joined',
      className: 'text-sm text-muted-foreground',
      cell: (row) => formatDate(row.joiningDate),
    },
  ];

  return (
    <div>
      <PageHeader
        title="People"
        description="Everyone with access to this workspace, and what they can do."
        actions={
          <Can permission="admin.user.create">
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4" />
              Invite someone
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search name, email or job title…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[9rem]"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INVITED">Invited</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="DEACTIVATED">Deactivated</option>
        </Select>
        <Select
          value={roleId}
          onChange={(event) => {
            setRoleId(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[9rem]"
        >
          <option value="">All roles</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load people.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => setSelected(row)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="Nobody here yet"
        emptyDescription="Invite your first colleague to get started."
        emptyAction={
          <Can permission="admin.user.create">
            <Button onClick={() => setInviteOpen(true)}>
              <Users className="h-4 w-4" />
              Invite someone
            </Button>
          </Can>
        }
      />

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} roles={roles} />
      <UserDetailDialog
        user={selected}
        roles={roles}
        onOpenChange={(open) => !open && setSelected(null)}
      />
    </div>
  );
}
